import { z } from 'zod';
import { HeureSchema, LangueSchema, SlugSchema, TexteBilingueSchema } from './textes.js';
/** Usage d'une adresse (section 3.1 : une galerie peut avoir plusieurs lieux). */
export const USAGES_ADRESSE = ['principale', 'secondaire', 'bureau', 'reserve'];
export const UsageAdresseSchema = z.enum(USAGES_ADRESSE);
export const AdresseSchema = z.object({
    usage: UsageAdresseSchema,
    /** Nom du lieu quand il diffère du nom de la galerie (« Espace Pantin »). */
    libelle: TexteBilingueSchema.optional(),
    rue: z.string().min(1),
    complement: z.string().min(1).optional(),
    codePostal: z.string().min(1),
    ville: z.string().min(1),
    pays: z.string().length(2, 'code pays ISO 3166-1 alpha-2'),
    latitude: z.number().min(-90).max(90).optional(),
    longitude: z.number().min(-180).max(180).optional(),
    /** Lien vers un plan externe, affiché en texte (aucune icône, 2.5). */
    planUrl: z.url().optional(),
    telephone: z.string().min(1).optional(),
});
export const JOURS = [
    'lundi',
    'mardi',
    'mercredi',
    'jeudi',
    'vendredi',
    'samedi',
    'dimanche',
];
export const JourSchema = z.enum(JOURS);
export const CreneauSchema = z
    .object({ ouverture: HeureSchema, fermeture: HeureSchema })
    .refine((c) => c.ouverture < c.fermeture, 'la fermeture doit suivre l’ouverture');
/**
 * Plage d'horaires : « mardi — samedi, 11 h — 19 h ». Deux créneaux pour une
 * fermeture le midi. Structuré, donc exploitable en JSON-LD.
 */
export const PlageHoraireSchema = z.object({
    du: JourSchema,
    au: JourSchema,
    creneaux: z.array(CreneauSchema).min(1),
});
export const HorairesSchema = z.object({
    plages: z.array(PlageHoraireSchema),
    /** « Et sur rendez-vous », « fermé en août » : texte libre bilingue. */
    note: TexteBilingueSchema.optional(),
});
export const RESEAUX = ['instagram', 'artsy', 'facebook', 'linkedin', 'vimeo', 'youtube'];
export const ReseauSchema = z.enum(RESEAUX);
export const LienReseauSchema = z.object({
    reseau: ReseauSchema,
    /** Libellé affiché : jamais une icône (2.5). */
    libelle: z.string().min(1),
    url: z.url(),
});
export const LanguesSchema = z
    .object({
    defaut: LangueSchema,
    actives: z.array(LangueSchema).min(1),
})
    .refine((l) => l.actives.includes(l.defaut), 'la langue par défaut doit faire partie des langues actives')
    .refine((l) => new Set(l.actives).size === l.actives.length, 'une langue ne peut être active deux fois');
export const GalerieSchema = z.object({
    nom: z.string().min(1),
    slug: SlugSchema,
    /** Domaine servi, sans protocole (D11 : `<slug>.artosera.gallery` par défaut). */
    domaine: z
        .string()
        .min(1)
        .regex(/^[a-z0-9.-]+$/, 'domaine invalide : minuscules, chiffres, points et tirets'),
    baseline: TexteBilingueSchema.optional(),
    description: TexteBilingueSchema,
    langues: LanguesSchema,
    adresses: z.array(AdresseSchema).min(1),
    horaires: HorairesSchema,
    email: z.email(),
    telephone: z.string().min(1).optional(),
    reseaux: z.array(LienReseauSchema),
    /** « Site propulsé par Artosera », désactivable dès l'offre Galerie (2.4). */
    mentionArtosera: z.boolean(),
});
/** Adresse à montrer par défaut : la principale, sinon la première. */
export function adressePrincipale(galerie) {
    const trouvee = galerie.adresses.find((a) => a.usage === 'principale');
    const premiere = galerie.adresses[0];
    if (trouvee !== undefined)
        return trouvee;
    if (premiere === undefined)
        throw new Error('la galerie doit porter au moins une adresse');
    return premiere;
}
/** Adresses publiques du site : la réserve n'est pas une adresse de visite. */
export function adressesPubliques(galerie) {
    return galerie.adresses.filter((a) => a.usage !== 'reserve');
}
//# sourceMappingURL=galerie.js.map